import { supabase } from '@/config/supabase'
import { useAuthStore } from '@/stores/auth.store'
import { usePortalStore } from '@/stores/portal.store'
import type { Contrato } from '@/types/database'
import type { ListFilters, PaginatedResult } from './associado.service'

// SELECT mínimo para a lista (apenas campos confirmados que existem)
const SELECT_LIST = '*, associado:veic_associados(nome,cpf,telefone), veiculo:veic_veiculos(placa,modelo,marca,cor)'

// SELECT completo para o drawer de detalhes
const SELECT_DETAIL = '*, associado:veic_associados(*), veiculo:veic_veiculos(*), faturas:veic_faturas(*), servicos:veic_contrato_servicos(*, servico:veic_servicos(*))'

function mapContrato(c: any): ContratoExpanded {
    const faturas: any[] = c.ultima_fatura ?? []
    const ultimaFatura = faturas.sort((a: any, b: any) =>
        new Date(b.data_vencimento).getTime() - new Date(a.data_vencimento).getTime()
    )[0]
    return {
        ...c,
        associado_nome: c.associado?.nome,
        associado_cpf: c.associado?.cpf,
        associado_telefone: c.associado?.telefone,
        veiculo_placa: c.veiculo?.placa,
        veiculo_modelo: c.veiculo?.modelo,
        veiculo_marca: c.veiculo?.marca,
        ultima_fatura_valor: ultimaFatura?.valor ?? null,
        ultima_fatura_status: ultimaFatura?.status ?? null,
    }
}

export type ContratoExpanded = Contrato & {
    associado_nome?: string
    associado_cpf?: string
    associado_telefone?: string
    veiculo_placa?: string
    veiculo_modelo?: string
    veiculo_marca?: string
    plano?: string
    ultima_fatura_valor?: number | null
    ultima_fatura_status?: string | null
    faturas?: any[]
}

export interface ContratoFilters extends ListFilters {
    associado_id?: string
    parceiro_id?: string
    tipo?: 'todos' | 'veicular' | 'cpf'
    servico?: string
    ativacao?: 'todos' | 'manual' | 'integrada'
    data_de?: string
    data_ate?: string
    cpf?: string
}

export const contratoService = {
    async list(filters: ContratoFilters = {}): Promise<PaginatedResult<ContratoExpanded>> {
        const { search, status, associado_id, parceiro_id, tipo, servico, ativacao, data_de, data_ate, cpf, page = 1, pageSize = 10 } = filters

        const authUser = useAuthStore.getState().user
        const portalCpf = cpf || usePortalStore.getState().cpf

        if (!authUser && portalCpf) {
            // Portal Mode: Fetch securely via RPC using CPF to get unified history
            // @ts-ignore
            const { data, error } = await supabase.schema('veicular_system').rpc('get_portal_contratos_por_cpf', { p_cpf: portalCpf })
            if (error) throw error
            const items = ((data as any[]) || []).map(mapContrato)
            return { data: items, total: items.length, page: 1, pageSize: 50, totalPages: 1 }
        }

        let query = supabase
            .schema('veicular_system')
            .from('veic_contratos')
            .select(SELECT_LIST, { count: 'exact' })
            .is('deleted_at', null)
            .order('created_at', { ascending: false })

        if (search) {
            query = query.or(`external_reference.ilike.%${search}%,plano.ilike.%${search}%`)
        }
        if (status && status !== 'todos') {
            query = query.eq('status', status)
        }
        if (associado_id) {
            query = query.eq('associado_id', associado_id)
        }
        if (parceiro_id) {
            query = query.eq('metadata->>parceiro_id', parceiro_id)
        }
        if (tipo === 'veicular') {
            query = query.not('veiculo_id', 'is', null)
        } else if (tipo === 'cpf') {
            query = query.is('veiculo_id', null)
        }
        if (servico && servico !== 'todos') {
            query = query.or(`metadata->>servico_nome.ilike.%${servico}%,plano.ilike.%${servico}%`)
        }
        if (ativacao === 'manual') {
            query = query.eq('metadata->>ativacao_tipo', 'manual')
        } else if (ativacao === 'integrada') {
            query = query.eq('metadata->>ativacao_tipo', 'integrada')
        }
        if (data_de) {
            query = query.gte('data_inicio', data_de)
        }
        if (data_ate) {
            query = query.lte('data_inicio', data_ate)
        }
        const from = (page - 1) * pageSize
        query = query.range(from, from + pageSize - 1)

        const { data, count, error } = await query
        if (error) throw error
        return {
            data: (data || []).map(mapContrato),
            total: count || 0, page, pageSize,
            totalPages: Math.ceil((count || 0) / pageSize),
        }
    },

    async getById(id: string): Promise<ContratoExpanded | null> {
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_contratos')
            .select(SELECT_DETAIL)
            .eq('id', id).is('deleted_at', null).single()
        if (error) throw error
        return mapContrato(data)
    },

    async create(input: Partial<Contrato>): Promise<Contrato> {
        // @ts-ignore
        const { data, error } = await supabase.schema('veicular_system').from('veic_contratos').insert(input as any).select().single()
        if (error) throw error
        return data as Contrato
    },

    async update(id: string, input: Partial<Contrato>): Promise<Contrato> {
        // @ts-ignore
        const { data, error } = await supabase.schema('veicular_system').from('veic_contratos').update(input as any).eq('id', id).select().single()
        if (error) throw error
        return data as Contrato
    },

    async cancel(id: string): Promise<void> {
        // Uses the DB function for proper cascading
        const { error } = await supabase.schema('veicular_system').rpc('cancelar_contrato' as any, {
            p_contrato_id: id,
            p_tenant_id: useAuthStore.getState().tenant?.id,
        } as any)
        if (error) throw error
    },

    async checkoutPortal(input: {
        associado_id: string
        servico_id: string
        servico_ids?: string[]
        veiculo_id?: string | null
        placa?: string | null
        tenant_id: string
        dia_vencimento: number
        valor_mensal: number
        valor_adesao: number
        adesao_pro_rata: boolean
        termo_aceite_em: string | null
        assinatura_digital?: any
    }): Promise<{ ok: boolean; contrato_id: string; adesao_url: string | null }> {
        const { data, error } = await supabase.functions.invoke('checkout-contratar', {
            body: input
        })
        if (error) throw error
        return data
    },
}
